import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useUserContext } from '@context/UserContext';
import { useCallback, useEffect } from 'react';
import { RumBusinessEventAction } from 'types/RUM.types';

// TODO: When page-level locking is introduced, consider a more granular key (e.g., auriga_edit_start_{deckId}_{pageId})
const STORAGE_KEY_PREFIX = 'auriga_edit_start_';

interface RecordSaveDurationOptions {
  componentId?: string;
  component?: string;
}

/**
 * Tracks the duration between the start of an editing session and a successful save,
 * then emits a RUM business event. Uses localStorage to persist the reference timestamp
 * so that subsequent saves measure from the last save rather than the original lock time.
 */
const useEditDurationTracker = () => {
  const { deckLockStatus, currentDeckId, currentDeck } = useDeckContext();
  const { username } = useUserContext();
  const { rumLogger } = useAurigaContext();

  const storageKey = currentDeckId ? `${STORAGE_KEY_PREFIX}${currentDeckId}` : null;

  // Sync lockedAt to localStorage (max-wins: only write if greater than stored value)
  useEffect(() => {
    if (!storageKey || !deckLockStatus.lockedAt) return;

    // lockedAt is an AWSDateTime (ISO-8601) string; parse to epoch ms.
    const candidateMs = new Date(deckLockStatus.lockedAt).getTime();
    if (Number.isNaN(candidateMs)) return;

    const stored = localStorage.getItem(storageKey);
    const storedMs = stored ? parseInt(stored, 10) : 0;

    if (candidateMs > storedMs) {
      localStorage.setItem(storageKey, String(candidateMs));
    }
  }, [deckLockStatus.lockedAt, storageKey]);

  /**
   * Records a RUM business event measuring the duration since the last save (or initial deck lock).
   * After emitting, updates the localStorage reference timestamp so subsequent saves measure from this point.
   *
   * No-ops if rumLogger is unavailable, no deck is selected, or no reference timestamp exists in localStorage.
   *
   * @param options - Optional context fields to enrich the event payload
   * @param options.componentId - Identifier of the component being saved (e.g., page PK)
   * @param options.component - Display name of the component being saved (e.g., page name)
   */
  const recordSaveDuration = useCallback(
    (options?: RecordSaveDurationOptions) => {
      if (!rumLogger || !storageKey || !currentDeckId) return;

      const stored = localStorage.getItem(storageKey);
      if (!stored) return;

      const startMs = parseInt(stored, 10);
      const now = Date.now();
      const duration = now - startMs;

      // TODO: Extend to support status: 'error' and errorMessage for failed save tracking
      rumLogger.recordBusinessEvent(RumBusinessEventAction.SAVE, {
        location: window.location.href,
        duration,
        deckId: currentDeckId,
        deckName: currentDeck?.deck_name ?? '',
        userAlias: username,
        status: 'success',
        ...(options?.componentId && { componentId: options.componentId }),
        ...(options?.component && { component: options.component }),
      });

      // Update reference timestamp for next save within same lock session
      localStorage.setItem(storageKey, String(now));
    },
    [rumLogger, storageKey, currentDeckId, currentDeck?.deck_name, username],
  );

  return { recordSaveDuration };
};

export default useEditDurationTracker;
